import { useCallback, useEffect, useState, createContext, useContext } from "react";

import { branding as brandingApi, type Branding } from "@/api/branding";

interface BrandingValue {
  data: Branding | null;
  refresh: () => Promise<void>;
}

const BrandingCtx = createContext<BrandingValue>({
  data: null,
  refresh: async () => undefined,
});

/** Read the current branding payload. May be null briefly while loading. */
export function useBranding(): Branding | null {
  return useContext(BrandingCtx).data;
}

/** Re-fetch branding after a save so colors/logo update live. */
export function useRefreshBranding(): () => Promise<void> {
  return useContext(BrandingCtx).refresh;
}

/**
 * Resolves branding once at boot from `/api/v1/branding`, applies the
 * primary color CSS variable, and updates the document title. Children
 * render immediately with the default brand while the request is in
 * flight — the brand swap is unobtrusive even on slow networks.
 */
export function BrandingLoader({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<Branding | null>(null);

  const load = useCallback(async () => {
    try {
      // The backend resolution chain (slug query → host header → default)
      // does the right thing for both dev (slug=default) and prod
      // (custom domain). Prefer query in dev because vite proxy strips
      // the original host.
      const slug = new URL(window.location.href).searchParams.get("org") ?? undefined;
      const b = await brandingApi.load(slug ?? "default");
      setData(b);
      applyBranding(b);
    } catch {
      // Branding is best-effort. Default styling already applied via CSS.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const value: BrandingValue = { data, refresh: load };
  return <BrandingCtx.Provider value={value}>{children}</BrandingCtx.Provider>;
}

function hexToRgbTriplet(hex: string): string | null {
  const h = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

function applyBranding(b: Branding) {
  const primary = b.branding.primary_color;
  if (primary) {
    const rgb = hexToRgbTriplet(primary);
    if (rgb) document.documentElement.style.setProperty("--brand-color", rgb);
  }
  if (b.name) {
    document.title = `${b.name} • Piggy`;
  }
  if (b.branding.favicon_url) {
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = b.branding.favicon_url;
  }
}
